import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, MessageSquare, Inbox, Phone, Mail, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'ahora mismo';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
}

const CHANNEL_META = {
  chat: { label: 'Chat', icon: MessageSquare, path: (t) => `/chat?conv=${t.conversation_id}` },
  whatsapp: { label: 'WhatsApp', icon: Phone, path: (t) => `/chat?conv=${t.conversation_id}` },
  order_message: { label: 'Mensajes de Pedido', icon: Inbox, path: (t) => `/order-messages?thread=${t.thread_id}` },
  support_email: { label: 'Email Soporte', icon: Mail, path: (t) => `/support-email?thread=${t.thread_id}` },
};

// Solo se muestran en el aviso hilos abiertos con actividad reciente (fácil de cambiar).
const ACTIVITY_WINDOW_DAYS = 7;
function isRecent(t) {
  const ts = t.last_message_at || t.updated_at;
  if (!ts) return false;
  const ageDays = (Date.now() - new Date(ts).getTime()) / 86400000;
  return ageDays <= ACTIVITY_WINDOW_DAYS;
}

/**
 * Banner de "otros hilos abiertos de este cliente" — se muestra dentro de un
 * hilo de Chat/WhatsApp/Mensajes de Pedido/Email cuando el `customer_id`
 * vinculado tiene otros hilos en cualquier canal. Distingue con una alerta
 * destacada los hilos donde YA se le respondió (para evitar duplicar trabajo),
 * mostrando la respuesta, el canal y la hora; y con un aviso más discreto los
 * que siguen sin respuesta en otro canal.
 *
 * currentType: "conversation" | "order_message" | "support_email"
 * currentId: conversation_id / thread_id actual (se excluye de la lista)
 */
export function CustomerThreadsBanner({ customerId, currentType, currentId }) {
  const [threads, setThreads] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!customerId) { setThreads(null); return; }
    let cancelled = false;
    api.get(`/api/customers/${customerId}/threads`).then(res => {
      if (cancelled) return;
      const convs = (res.data.conversations || [])
        .filter(c => !(currentType === 'conversation' && c.conversation_id === currentId))
        .map(c => ({ ...c, channelKey: c.type === 'whatsapp' ? 'whatsapp' : 'chat' }));
      const orders = (res.data.order_threads || [])
        .filter(t => !(currentType === 'order_message' && t.thread_id === currentId))
        .map(t => ({ ...t, channelKey: 'order_message' }));
      const emails = (res.data.email_threads || [])
        .filter(t => !(currentType === 'support_email' && t.thread_id === currentId))
        .map(t => ({ ...t, channelKey: 'support_email' }));
      const all = [...convs, ...orders, ...emails].filter(t => t.is_open !== false && isRecent(t));
      setThreads({
        answered: all.filter(t => t.answered_by_agent),
        waiting: all.filter(t => !t.answered_by_agent),
      });
    }).catch(() => setThreads(null));
    return () => { cancelled = true; };
  }, [customerId, currentType, currentId]);

  if (!threads) return null;
  const total = threads.answered.length + threads.waiting.length;
  if (total === 0) return null;

  const goTo = (t) => navigate(CHANNEL_META[t.channelKey].path(t));

  return (
    <div className="mx-4 mt-2 mb-1 space-y-2 overflow-hidden" data-testid="customer-threads-banner">
      {threads.answered.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs">
          <div className="flex items-start gap-1.5 text-amber-300 font-medium mb-1.5 min-w-0">
            <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
            <span className="min-w-0">Ya se le ha respondido a este cliente por otro canal — revisa antes de contestar de nuevo:</span>
          </div>
          <div className="space-y-1.5">
            {threads.answered.map((t) => {
              const meta = CHANNEL_META[t.channelKey];
              const Icon = meta.icon;
              return (
                <button
                  key={t.conversation_id || t.thread_id}
                  data-testid={`answered-elsewhere-${t.conversation_id || t.thread_id}`}
                  onClick={() => goTo(t)}
                  className="w-full flex items-start gap-2 text-left px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                >
                  <CheckCircle2 size={12} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-amber-200">
                      <Icon size={11} />
                      <span className="font-medium">{meta.label}</span>
                      <span className="text-amber-400/70">· {timeAgo(t.last_message_at)}</span>
                    </div>
                    {t.last_message && (
                      <p className="text-gray-300 truncate mt-0.5">"{t.last_message}"</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {threads.waiting.length > 0 && (
        <div
          data-testid="customer-threads-waiting-banner"
          className="flex flex-wrap items-center gap-2 rounded-lg border border-indigo-500/30 bg-indigo-500/5 px-3 py-2 text-xs text-indigo-200"
        >
          <Users size={13} className="text-indigo-400 flex-shrink-0" />
          <span className="flex-shrink-0">
            Este cliente tiene {threads.waiting.length} conversación{threads.waiting.length > 1 ? 'es' : ''} más abierta{threads.waiting.length > 1 ? 's' : ''}:
          </span>
          <div className="flex flex-wrap gap-1.5">
            {threads.waiting.map((t) => {
              const meta = CHANNEL_META[t.channelKey];
              const Icon = meta.icon;
              return (
                <button
                  key={t.conversation_id || t.thread_id}
                  data-testid={`related-${t.channelKey}-${t.conversation_id || t.thread_id}`}
                  onClick={() => goTo(t)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-600/20 border border-indigo-500/30 hover:bg-indigo-600/40 transition-colors max-w-full min-w-0"
                  title={t.last_message || t.external_order_id || ''}
                >
                  <Icon size={10} className="flex-shrink-0" />
                  <span className="truncate min-w-0">
                    {meta.label}{t.channelKey === 'order_message' && t.external_order_id && t.external_order_id !== '0' ? ` #${t.external_order_id}` : ''}
                  </span>
                  <Clock size={9} className="opacity-60 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
